// 组长 / 值班长处置动作日志（演示数据，前端本地留存）。
// 真实实现时换成 /operations/logs 一类接口即可；UI 与记录口径不变。
// 口径：一条日志 = 谁在什么时候对哪件事做了什么；组长只看自己的组内处置，值班长看全场调配与事件。
// 注意：演示版按浏览器 localStorage 单机留存，换账号/换组仍会看到同一浏览器上的历史；
// 真实实现必须按 tenant / group / user 维度落库并做范围过滤。

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type OpsLogScope = 'leader' | 'duty';

export interface OpsLogEntry {
  id: string;
  /** 记录归属：leader=组长（本组现场处置）；duty=值班长（全场调配 / 事件 / 长期配置） */
  scope: OpsLogScope;
  at: number;
  actor: string;
  /** 动作短语：接单 / 监听 / 接管 / 跨组调人 / 新建重大事件 / 关闭… */
  action: string;
  /** 具体对象与结果，一句话说清 */
  detail: string;
}

interface OpsLogState {
  entries: OpsLogEntry[];
  record: (input: Omit<OpsLogEntry, 'id' | 'at'> & { at?: number }) => void;
}

let seq = 0;
const now = Date.now();

/** 演示种子：让组长/值班长第一次进来就能看到「日志长什么样、记了什么」 */
const SEED: OpsLogEntry[] = [
  {
    id: 'seed-leader-1',
    scope: 'leader',
    at: now - 8 * 60_000,
    actor: '组长 · 罗敏（示例）',
    action: '接单',
    detail: '坐席1005 举手（要授权）：客户要求退 30 元，已承诺 2 分钟内给口径',
  },
  {
    id: 'seed-leader-2',
    scope: 'leader',
    at: now - 21 * 60_000,
    actor: '组长 · 罗敏（示例）',
    action: '监听',
    detail: '监听坐席1002 通话，确认客户听不到声音，已让坐席换线重拨',
  },
  {
    id: 'seed-leader-3',
    scope: 'leader',
    at: now - 46 * 60_000,
    actor: '组长 · 罗敏（示例）',
    action: '接管',
    detail: '接管坐席1008 情绪升级通话，客户已转至本人分机继续处理',
  },
  {
    id: 'seed-duty-1',
    scope: 'duty',
    at: now - 12 * 60_000,
    actor: '值班长 · 陈刚（示例）',
    action: '跨组调人',
    detail: '从「宽带报修」调 2 人（赵敏、林丹）到「客服咨询」支援，预计 16:10 到位',
  },
  {
    id: 'seed-duty-2',
    scope: 'duty',
    at: now - 35 * 60_000,
    actor: '值班长 · 陈刚（示例）',
    action: '新建重大事件',
    detail: '「3 号技能组溢出」新建，负责人陈刚，待 30 分钟后复查等待数',
  },
  {
    id: 'seed-duty-3',
    scope: 'duty',
    at: now - 64 * 60_000,
    actor: '值班长 · 陈刚（示例）',
    action: '调整长期配置',
    detail: '把「客服咨询」的排队溢出目标改为「宽带报修」，30 秒内生效',
  },
];

export const useOpsLog = create<OpsLogState>()(
  persist(
    (set) => ({
      entries: SEED,
      record: (input) =>
        set((s) => ({
          entries: [
            {
              ...input,
              id: `log-${Date.now()}-${++seq}`,
              at: input.at ?? Date.now(),
            },
            ...s.entries,
          ].slice(0, 200),
        })),
    }),
    { name: 'bcc-ops-log-demo' },
  ),
);

/** 组件里不便拿 hook 时（如事件回调）用它记账 */
export function recordOpsLog(input: Omit<OpsLogEntry, 'id' | 'at'> & { at?: number }) {
  useOpsLog.getState().record(input);
}
